/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Grid,
  Typography,
  Box,
  Tooltip,
  IconButton,
  Card,
  Chip,
  DialogContent,
  Divider,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from 'utils/makeStylesAdapter';
import { makeStyles } from 'utils/makeStylesAdapter';
import { InputConsensus, ButtonConsensus, ComboBoxMultiSelect } from 'components';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { toast } from 'react-toastify';
import crimeService from 'services/crime/crimeService';
import lawService from 'services/law/lawService';
import HeaderModal from 'components/HeaderModal';
import { onCloseWithBackdropDialog } from 'utils/materialUI/backdropClickDialog';
import { colors } from 'utils/colorsSteps';
import { isInfraction } from 'utils/isInfraction';
import { AutoCompleteConfig } from 'constants/autocomplete';
import LazyAutocomplete from 'components/LazyAutocomplete';

const useStyles = makeStyles(theme => ({
  root: {
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  },
  body: {
    padding: theme.spacing(3),
    maxHeight: 'calc(85vh - 120px)',
    overflow: 'auto',
  },
  titulo: {
    color: '#00597b',
    fontWeight: 'bold',
    textAlign: 'left',
    fontSize: 20,
    fontFamily: 'roboto-regular',
  },
  smallTitulo: {
    color: '#00597b',
    fontWeight: 'bold',
    textAlign: 'left',
    fontSize: 16,
    fontFamily: 'roboto-regular',
  },
  warningBox: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffc107',
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  warningText: {
    color: '#856404',
    fontSize: 15,
    fontWeight: 500,
  },
  containerLabelInput: {
    paddingLeft: 10,
    paddingRight: 10,
    paddingBottom: 10,
  },
  containerBtn: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  containerBtnArea: {
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderTop: '2px solid #e0e0e0',
    backgroundColor: '#f5f5f5',
  },
  addCrimeContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 5,
    minHeight: 80,
    maxHeight: 160,
    overflow: 'auto',
    border: '1px solid #999',
    borderRadius: 5,
    marginTop: 5,
  },
  textoCrimeContainer: {
    color: '#00b1ac',
    fontWeight: 'bold',
    borderBottom: '1px solid #999',
    paddingBottom: 0,
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: 40,
    textAlign: 'justify',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  chip: {
    margin: 2,
    backgroundColor: '#00b1ac',
    color: '#ffffff',
  },
  cardCrime: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'start',
    boxShadow: 'none',
    backgroundColor: 'transparent',
    minHeight: 40,
    overflowX: 'auto',
    marginBottom: 10,
  },
  inputMaterial: {
    width: '100%',
  },
  divider: {
    margin: theme.spacing(3, 0),
    backgroundColor: '#00597b',
    height: 2,
  },
}));

const ModalConfirmCrimesAndSubjects = props => {
  const { open, onClose, onConfirm, inquiry, inquiryClassCode, disabled = false } = props;

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const classes = useStyles();

  const [crimes, setCrimes] = useState([]);
  const [selectedCrime, setSelectedCrime] = useState(null);
  const [selectedLaw, setSelectedLaw] = useState(null);
  const [subtypesSelected, setSubtypesSelected] = useState([]);
  const [subtypeOptions, setSubtypeOptions] = useState([]);
  const [editingSubtypes, setEditingSubtypes] = useState(false);
  const [subjects, setSubjects] = useState([]);

  useEffect(() => {
    if (open && inquiry) {
      const initialCrimes = inquiry.crimes || [];
      setCrimes(initialCrimes);
      updateSubjectsFromCrimes(initialCrimes);
    }
  }, [open, inquiry]);

  useEffect(() => {
    setSubtypeOptions(selectedCrime?.subtypes);
  }, [selectedCrime]);

  const updateSubjectsFromCrimes = crimesList => {
    const uniqueSubjects = [];
    const seenHierarchies = new Set();

    crimesList.forEach(crime => {
      if (
        crime.subject &&
        crime.subject.hierarchy &&
        !seenHierarchies.has(crime.subject.hierarchy)
      ) {
        seenHierarchies.add(crime.subject.hierarchy);
        uniqueSubjects.push(crime.subject);
      }
    });

    setSubjects(uniqueSubjects);
  };

  const fetchCrimes = async (search, page) => {
    const params = {
      lawId: selectedLaw?.id,
      page,
      size: AutoCompleteConfig.PAGINATION_SIZE,
      search,
      isInfraction: isInfraction(inquiryClassCode),
    };

    const result = await crimeService.getCrimesService(params);

    return {
      data: result.data.data,
      lastPage: result.data.lastPage,
    };
  };

  const fetchLaws = async (search, page) => {
    const params = {
      page,
      size: 20,
      search,
    };

    const result = await lawService.getLawService(params);

    return {
      data: result.data.data,
      lastPage: result.data.lastPage,
    };
  };

  const handleChangeLaw = (event, newValue) => {
    setSelectedLaw(newValue);
  };

  const handleChangeCrimeAutocomplete = (event, newValue) => {
    setSelectedCrime(newValue);
  };

  const handleAddCrime = () => {
    const newValue = selectedCrime;

    if (!newValue) {
      return toast.warn('Selecione um crime para ser adicionado');
    }

    const crimeExists = crimes.some(crime => crime.id === newValue.id);

    if (crimeExists) {
      return toast.warn('O crime selecionado já existe na lista');
    }

    if (subtypesSelected?.length) {
      const subtypes = subtypesSelected.map(sub => subtypeOptions.find(item => item.id === sub));
      newValue.subtypesSelected = subtypes;
    }

    const updatedCrimes = [...crimes, newValue];
    setCrimes(updatedCrimes);
    updateSubjectsFromCrimes(updatedCrimes);

    setSelectedCrime(null);
    setSubtypesSelected([]);
  };

  const handleRemoveCrime = (event, crimeId) => {
    const newCrimes = crimes.filter(crime => crime.id !== crimeId);
    setCrimes(newCrimes);
    updateSubjectsFromCrimes(newCrimes);
  };

  const changeSubtypesSelected = () => {
    const index = crimes.findIndex(crime => crime.id === selectedCrime.id);
    const subtypes = subtypesSelected?.map(sub => {
      return subtypeOptions.find(subtype => subtype.id === sub.id || subtype.id === sub);
    });

    if (index !== -1) {
      const updatedCrimes = [...crimes];

      updatedCrimes[index] = {
        ...updatedCrimes[index],
        subtypesSelected: subtypes || [],
      };

      setCrimes(updatedCrimes);
    }

    setSelectedCrime({ ...selectedCrime, subtypesSelected: subtypes });
  };

  const handleChangeCrime = () => {
    if (!selectedCrime) {
      return toast.warn('Selecione um crime para ser adicionado');
    }
    if (!editingSubtypes) {
      handleAddCrime();
    } else {
      changeSubtypesSelected();
    }
    setSelectedCrime(null);
    setSubtypesSelected([]);
    setEditingSubtypes(false);
  };

  const handleEditSubtype = crime => {
    setSubtypesSelected(crime.subtypesSelected);
    setSelectedCrime(crime);
    setEditingSubtypes(true);
  };

  const handleChangeSubtype = newValue => {
    setSubtypesSelected(newValue);
  };

  const handleConfirm = () => {
    if (crimes.length === 0) {
      return toast.warn('Adicione pelo menos um crime');
    }

    onConfirm({ crimes, subjects });
  };

  return (
    <div className={classes.root}>
      <Dialog
        fullWidth
        fullScreen={fullScreen}
        onClose={(event, reason) => onCloseWithBackdropDialog(event, reason, onClose)}
        open={open}
        aria-labelledby="modal-confirm-crimes-subjects"
        maxWidth="lg"
      >
        <HeaderModal onClose={onClose}>Confirmação de Crimes/Assuntos do inquérito</HeaderModal>
        <DialogContent className={classes.body}>
          <Box className={classes.warningBox}>
            <Typography className={classes.warningText}>
              ⚠️ O Promotor deve atualizar crimes/assuntos do inquérito antes da conclusão. De
              acordo com a análise realizada nessa fase, os crimes/assuntos atuais podem ser
              ajustados. É possível incluir novos crimes e alterar/excluir crimes existentes.
            </Typography>
          </Box>

          <Typography className={classes.titulo}>
            {isInfraction(inquiryClassCode) ? 'Atos Infracionais' : 'Crimes'}
          </Typography>

          <Grid container spacing={1} style={{ marginTop: 5 }}>
            <Grid size={{ xl: 6, lg: 6, md: 6, sm: 6, xs: 12 }}>
              <div className={classes.containerLabelInput}>
                <LazyAutocomplete
                  value={selectedLaw}
                  onChange={handleChangeLaw}
                  getOptionLabel={option => option?.name || ''}
                  renderOption={(props, option) => (
                    <li {...props}>
                      {option.name} - {option.number}
                    </li>
                  )}
                  enablePagination
                  fetchOptions={fetchLaws}
                  renderInput={params => (
                    <InputConsensus
                      {...params}
                      label="Lei"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {params.InputProps.loading ? (
                              <CircularProgress color="primary" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </div>
            </Grid>
            <Grid size={{ xl: 6, lg: 6, md: 6, sm: 6, xs: 12 }}>
              <div className={classes.containerLabelInput}>
                <LazyAutocomplete
                  value={selectedCrime}
                  onChange={handleChangeCrimeAutocomplete}
                  getOptionLabel={option => option?.taxonomy || ''}
                  enablePagination
                  disabled={editingSubtypes}
                  fetchOptions={fetchCrimes}
                  renderInput={params => (
                    <InputConsensus
                      {...params}
                      name="selectedCrime"
                      variant="standard"
                      label={`${
                        isInfraction(inquiryClassCode) ? 'Ato Infracional' : 'Crime'
                      } cometido *`}
                      fullWidth
                      onClick={() => {
                        setSubtypeOptions(null);
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {params.InputProps.loading ? (
                              <CircularProgress color="primary" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  refreshKey={selectedLaw?.id || inquiryClassCode}
                />
              </div>
            </Grid>
            <Grid size={{ xl: 6, lg: 6, md: 6, sm: 6, xs: 12 }}>
              {subtypeOptions?.length > 0 && (
                <div className={classes.containerLabelInput}>
                  <ComboBoxMultiSelect
                    options={subtypeOptions.filter(
                      option =>
                        option.isActive || subtypesSelected?.some(s => (s?.id ?? s) === option.id),
                    )}
                    label="Selecione os subtipos"
                    selectedIds={subtypesSelected?.map(subtype => subtype?.id ?? subtype)}
                    onChange={handleChangeSubtype}
                  />
                </div>
              )}
            </Grid>
            <Grid
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                paddingRight: 15,
              }}
              size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
              <ButtonConsensus
                title={editingSubtypes ? 'Alterar' : 'Adicionar'}
                backgroundColor={colors.primary}
                hovercolor={colors.primary}
                size="small"
                onClick={handleChangeCrime}
              />
            </Grid>
            <Grid
              style={{ paddingLeft: 10, paddingRight: 10, marginBottom: 10 }}
              size={{ xs: 12, sm: 12 }}>
              <div className={classes.addCrimeContainer}>
                <Typography className={classes.smallTitulo}>
                  {isInfraction(inquiryClassCode) ? 'Ato(s) Infracional(is)' : 'Crime(s)'}
                </Typography>
                {crimes.length === 0 && (
                  <Typography style={{ color: '#999', fontSize: 13, marginTop: 10 }}>
                    Nenhum crime adicionado
                  </Typography>
                )}
                {crimes.map(crime => (
                  <div key={crime.id} className={classes.textoCrimeContainer}>
                    <Typography>
                      {crime.taxonomy} - Lei: {crime.lawNumber ? crime.lawNumber : 'não informada'}{' '}
                      - Artigo: {crime.article ? crime.article : 'não informado'}{' '}
                      {crime.paragraph ? '-' : ''} {crime.paragraph ? crime.paragraph : ''}{' '}
                      {crime.item ? '-' : ''} {crime.item ? crime.item : ''}{' '}
                      {crime.point ? '-' : ''} {crime.point ? crime.point : ''}{' '}
                      {crime.subtypesSelected?.length
                        ? `- Subtipos: ${crime.subtypesSelected.map(obj => obj.name).join(', ')}`
                        : ''}
                    </Typography>
                    <div className={classes.iconContainer}>
                      {crime.subtypes?.length > 0 && (
                        <EditIcon
                          fontSize="small"
                          onClick={() => handleEditSubtype(crime)}
                          style={{ cursor: 'pointer' }}
                        />
                      )}
                      <DeleteIcon
                        fontSize="small"
                        onClick={event => handleRemoveCrime(event, crime.id)}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Grid>
          </Grid>

          <Divider className={classes.divider} />

          <Typography className={classes.titulo}>Assuntos Relacionados ao Crime</Typography>

          <Grid container spacing={1} style={{ marginTop: 10, paddingLeft: 10, paddingRight: 10 }}>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
              <Card className={classes.cardCrime}>
                {subjects.length > 0 ? (
                  subjects.map((subject, index) => (
                    <Tooltip key={index} title={subject.hierarchy} arrow placement="bottom">
                      <Chip
                        className={classes.chip}
                        size="small"
                        label={[...subject.hierarchy.split(':')].pop()}
                      />
                    </Tooltip>
                  ))
                ) : (
                  <Chip className={classes.chip} size="small" label="Nenhum assunto relacionado" />
                )}
              </Card>
            </Grid>
          </Grid>
        </DialogContent>

        <Box className={classes.containerBtnArea}>
          <Grid container spacing={2} justifyContent="center">
            <Grid>
              <ButtonConsensus
                title="Cancelar"
                backgroundColor="#fff"
                hovercolor={colors.greySecundary}
                color={colors.greyShadow}
                onClick={onClose}
              />
            </Grid>
            <Grid>
              <ButtonConsensus
                title="Concluir e Tramitar"
                backgroundColor={colors.primary}
                hovercolor={colors.primary}
                color="#fff"
                onClick={handleConfirm}
                disabled={disabled}
              />
            </Grid>
          </Grid>
        </Box>
      </Dialog>
    </div>
  );
};

export default ModalConfirmCrimesAndSubjects;
